import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IListFilter } from '@app/api/interfaces/common/list-filter';
import { IPaginationMeta, IPaginationRequest } from '@app/api/interfaces/common/pagination-meta.interface';
import { prepareParamsForRequest } from '@app/core/methods/http-params';
import { SplitRequestService } from '@app/core/services/split-request-by-limit/split-request.service';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { IComment, IUpdateCommentRequest } from '@app/api/interfaces/comment.interface';
import { CorrelationsMockService } from '@mock/data-services/correlations-mock.service';
import { CorrelationMode } from '@app/api/enums/correlation-mode.enum';

@Injectable({
  providedIn: 'root',
})
export class CorrelationsApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly splitRequestService: SplitRequestService,
    private readonly MOCK: CorrelationsMockService
  ) {}

  public getByProjectId(
    mode = CorrelationMode.CORRELATIONS,
    projectId: string,
    filter: IListFilter = {},
    pagination: Partial<IPaginationRequest> = {
      limit: 1000,
    },
    all = true
  ): Observable<IApiResponse<ICorrelation[], IPaginationMeta>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.getAll(projectId, filter);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/${mode}`;

    const filterForParams = prepareParamsForRequest(filter);

    const params = new HttpParams({
      fromObject: {
        ...(filterForParams || {}),
        ...(pagination || {}),
      },
    });

    const options = {
      params,
      withCredentials: true,
    };

    if (all) {
      return this.splitRequestService.get<ICorrelation, IPaginationMeta>(url, options);
    } else {
      return this.httpClient.get<IApiResponse<ICorrelation[], IPaginationMeta>>(url, options);
    }
  }

  public getOne(
    mode = CorrelationMode.CORRELATIONS,
    projectId: string,
    correlationId: string
  ): Observable<IApiResponse<ICorrelation, object>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.getOne(mode, projectId, correlationId);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/${mode}/${correlationId}`;

    return this.httpClient.get<IApiResponse<ICorrelation, object>>(url, {
      withCredentials: true,
    });
  }

  public updateComment(
    mode = CorrelationMode.CORRELATIONS,
    projectId: string,
    correlationId: string,
    body: IUpdateCommentRequest
  ): Observable<IApiResponse<IComment, object>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.updateComment(mode, projectId, correlationId, body);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/${mode}/${correlationId}/comment`;

    return this.httpClient.patch<IApiResponse<IComment, object>>(url, body, {
      withCredentials: true,
    });
  }
}
